import React, { use, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PointClickEventObject } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts';
import { bankersRound } from '@components-clean/core/charts/shared/number-utils';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { buildExactMatchTrackedChange } from '@components-clean/core/filter-ui/action-bar/shared/filter.service';
import PanelFrame from '@components-clean/core/panel-frame';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { SeriesDataDonut } from '@interfaces-clean/panels.interface';
import { RiskDashboardContext } from '@pages-clean/risk-assessment/risk-dashboard/context';
import { appendScreenFiltersQuery } from '@pages-clean/risk-assessment/risk-dashboard/sections/chart-utils';
import { PanelType, RiskReviewStatusType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { mapRiskReviewStatus } from '@utils/risk-helper';
import { AllPaths } from '@utils/router/all-paths';
import { CompleteRiskFields } from '@queries/generated/fragments.generated';

const RiskDashboardReviewStatus = () => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskDashboard.riskReviewStatus' });
    const [chartData, setChartData] = useState<SeriesDataDonut[]>([]);
    const navigate = useNavigate();
    const {
        filteredRisks: risks,
        filterableRisks,
        categories,
        screenTrackedChanges,
        includeObsolete,
        filterVersion,
    } = use(RiskDashboardContext);

    const handleClick = (event: PointClickEventObject) => {
        NavigationService.setViewRiskDetails();

        const parentCategory = categories.find((c) => c.items.some((item) => item.fieldKey === 'reviewStatus'));
        const reviewStatusCategory = parentCategory?.items.find((item) => item.fieldKey === 'reviewStatus');
        const clickTrackedChanges = reviewStatusCategory
            ? [
                  buildExactMatchTrackedChange(
                      filterableRisks,
                      reviewStatusCategory,
                      event.point.name,
                      parentCategory!.id,
                  ),
              ]
            : [];

        navigate(
            appendScreenFiltersQuery(
                AllPaths.path('ra-risk-log') ?? '',
                clickTrackedChanges,
                screenTrackedChanges,
                includeObsolete,
            ),
        );
    };

    const reviewStatuses = [
        { status: RiskReviewStatusType.NOT_REVIEWED, color: styles['rag-colour-grey'] },
        { status: RiskReviewStatusType.REVIEW_OVERDUE, color: styles['rag-colour-red'] },
        { status: RiskReviewStatusType.REVIEW_DUE, color: styles['rag-colour-amber'] },
        { status: RiskReviewStatusType.REVIEWED, color: styles['rag-colour-green'] },
    ];

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setChartData(
            reviewStatuses
                .map((reviewStatus): SeriesDataDonut => ({
                    name: mapRiskReviewStatus(reviewStatus.status),
                    count: risks.filter((r: CompleteRiskFields) => reviewStatus.status === r.reviewStatus).length,
                    color: reviewStatus.color,
                }))
                .filter((s) => s.count > 0),
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [risks]);

    return (
        <PanelFrame
            data-testid="risk-dashboard-review-status-chart"
            title={t('title')}
            showLegend
            panelType={PanelType.GRAPH}
            graphType="DONUT"
            panelData={chartData}
            version={filterVersion}
            hidePanelSetup
            customOptions={{
                colors: chartData.map((category) => category.color as string),
                plotOptions: {
                    pie: {
                        point: {
                            events: {
                                click: handleClick,
                            },
                        },
                    },
                },
                tooltip: {
                    pointFormatter() {
                        const point: Highcharts.Point = this;

                        return (
                            `<span>${t('numberOfRisks')}: ${point.y}</span><br/>` +
                            `<span>${t('percentage')}: ${bankersRound(point.percentage ?? 0, 2)}%</span>`
                        );
                    },

                    headerFormat: '<b>{point.key}</b><br/>',
                },
            }}
            customHeight={380}
        />
    );
};

export default React.memo(RiskDashboardReviewStatus);
